import { listenDatawrapperResize } from "@/lib/datawrapper-resize";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/dataviz")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { title: "Dataviz — Visualisations de données de Mathys Gautier" },
      {
        name: "description",
        content:
          "Une sélection des datavisualisations de Mathys Gautier : des infographies fondées sur des données publiques pour contextualiser et expliquer les phénomènes.",
      },
      { name: "robots", content: "noimageindex" },
      { property: "og:title", content: "Dataviz — Visualisations de données de Mathys Gautier" },

      {
        property: "og:description",
        content:
          "Une sélection des datavisualisations de Mathys Gautier : des infographies fondées sur des données publiques pour contextualiser et expliquer les phénomènes.",
      },
      { property: "og:url", content: "https://mathysgautier.com/dataviz" },
    ],
    links: [{ rel: "canonical", href: "https://mathysgautier.com/dataviz" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Dataviz — Visualisations de données de Mathys Gautier",
          description: "Une sélection des datavisualisations de Mathys Gautier : des infographies fondées sur des données publiques pour contextualiser et expliquer les phénomènes.",
          url: "https://mathysgautier.com/dataviz",
          isPartOf: {
            "@type": "WebSite",
            name: "Mathys Gautier",
            url: "https://mathysgautier.com",
          },
          about: {
            "@type": "Person",
            name: "Mathys Gautier",
            jobTitle: "Journaliste",
            url: "https://mathysgautier.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: "https://mathysgautier.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Dataviz",
              item: "https://mathysgautier.com/dataviz",
            },
          ],
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(datavizQueryOptions),
  errorComponent: ({ error }) => (
    <div role="alert" className="mx-auto max-w-6xl px-6 py-24 text-sm">
      {error instanceof Error ? error.message : "Une erreur est survenue."}
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-6xl px-6 py-24 text-sm">Contenu introuvable.</div>
  ),
  component: Dataviz,
});

function ToolsRow({ tools }: { tools: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
        Outils utilisés
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {tools.map((name) => (
          <span
            key={name}
            className="inline-flex items-center rounded-full border border-border bg-paper px-3 py-1 text-xs font-medium text-foreground/80 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-ocean hover:text-ocean"
          >
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

interface Chart {
  /** "chart" = iframe Datawrapper ; "site" = aperçu réactif d'un site web. */
  kind?: "chart" | "site";
  url: string;
  title?: string;
  aria_label?: string;
  ariaLabel?: string;
  height?: number;
  caption?: string;
}

interface DatavizProject {
  id: string;
  title: string;
  publication_type: string | null;
  date_label: string | null;
  description: string | null;
  tools: string[];
  charts: Chart[];
}

const datavizQueryOptions = queryOptions({
  queryKey: ["dataviz-projects"],
  queryFn: async (): Promise<DatavizProject[]> => {
    const { data, error } = await supabase
      .from("dataviz_projects")
      .select(
        "id,title,publication_type,date_label,description,tools,charts,display_order,created_at",
      )
      .eq("is_published", true)
      .order("display_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r) => ({
      id: r.id,
      title: r.title,
      publication_type: r.publication_type,
      date_label: r.date_label,
      description: r.description,
      tools: (r.tools ?? []) as string[],
      charts: (Array.isArray(r.charts) ? r.charts : []) as unknown as Chart[],
    }));
  },
});

function Dataviz() {
  const { data: projects } = useSuspenseQuery(datavizQueryOptions);

  useEffect(() => listenDatawrapperResize(), []);

  return (
    <section className="mx-auto max-w-6xl px-6 pt-16 pb-24">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-background">
        <div className="absolute inset-x-0 top-0 h-px bg-ocean" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-px bg-ocean" aria-hidden />
        <header className="max-w-3xl px-8 pt-16 pb-10 sm:px-12 sm:pt-20 sm:pb-12">
          <p className="eyebrow text-ocean mb-12">Dataviz</p>
          <h1 className="mt-12 font-display text-5xl leading-[1.05] sm:text-6xl">
            Visualisations de <em className="text-ocean">données</em>
          </h1>
          <p className="mt-16 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Une petite sélection des datavisualisations réalisées ! On peut tout représenter avec de
            belles infographies pour mieux contextualiser et expliquer des phénomènes... à condition
            que les données soient publiques et accessibles ! <br /> #ViveLOpenData
          </p>
        </header>
      </div>

      {projects.map((project, i) => {
        const isLast = i === projects.length - 1;
        const isFirst = i === 0;
        const eyebrow = [project.publication_type?.toUpperCase(), project.date_label]
          .filter(Boolean)
          .join("\u00A0— ");
        return (
          <div
            key={project.id}
            className={`${isFirst ? "mt-12" : "mt-16"} rounded-3xl border border-border bg-paper p-6 sm:p-10 shadow-[0_10px_40px_-24px_rgba(28,42,63,0.25)] ${isLast ? "mb-16" : ""}`}
          >
            {eyebrow && <p className="eyebrow text-ocean">{eyebrow}</p>}
            <h2 className="mt-4 font-serif text-3xl leading-tight sm:text-4xl">{project.title}</h2>
            {project.description && (
              <p className="mt-6 max-w-3xl text-muted-foreground whitespace-pre-line">
                {project.description}
              </p>
            )}

            {project.tools.length > 0 && (
              <div className="mt-8">
                <ToolsRow tools={project.tools} />
              </div>
            )}

            {project.charts.length > 0 && (
              <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
                {project.charts.map((chart, i) =>
                  chart.kind === "site" ? (
                    <figure
                      key={`${chart.url}-${i}`}
                      className="liquid-glass rounded-2xl p-3 lg:col-span-2"
                    >
                      <iframe
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                        title={chart.title ?? "Aperçu du site"}
                        aria-label={chart.aria_label ?? chart.ariaLabel ?? chart.title ?? ""}
                        src={chart.url}
                        frameBorder="0"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        className="w-full rounded-xl border border-border bg-background"
                        style={{ border: "none" }}
                        height={chart.height ?? 600}
                        data-external="1"
                      />
                      {chart.caption && (
                        <figcaption className="mt-2 px-2 text-xs text-muted-foreground">
                          {chart.caption}
                        </figcaption>
                      )}
                    </figure>
                  ) : (
                    <figure key={`${chart.url}-${i}`} className="liquid-glass rounded-2xl p-3">
                      <iframe
                        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                        title={chart.title ?? ""}
                        aria-label={chart.aria_label ?? chart.ariaLabel ?? chart.title ?? ""}
                        src={chart.url}
                        scrolling="no"
                        loading="lazy"
                        frameBorder="0"
                        style={{ width: 0, minWidth: "100%", border: "none" }}
                        height={chart.height ?? 420}
                        data-external="1"
                      />
                      {chart.caption && (
                        <figcaption className="mt-2 px-2 text-xs text-muted-foreground">
                          {chart.caption}
                        </figcaption>
                      )}
                    </figure>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}
      <p className="mt-20 text-center text-sm italic text-muted-foreground">
        D’autres visualisations arriveront prochainement…
      </p>
    </section>
  );
}
